/**
 * Knockout Bracket - Visual tournament bracket
 */
import { useState, useEffect } from 'react';
import { axiosInstance } from '../../config/axios';

export default function KnockoutBracket({ eventId }) {
  const [bracket, setBracket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBracket();
  }, [eventId]);

  const fetchBracket = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/v1/events/${eventId}/fixtures/bracket`);
      setBracket(res.data);
    } catch (err) {
      console.error('Failed to fetch bracket:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  if (!bracket || Object.values(bracket).every(round => round.length === 0)) {
    return (
      <div className="text-center py-12" data-testid="bracket-empty">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-brackets-curly text-2xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-2">No Knockout Matches</h3>
        <p className="text-sm text-gray-500">
          Knockout bracket will appear here when knockout rounds are scheduled.
        </p>
      </div>
    );
  }

  const roundLabels = {
    round_of_32: 'Round of 32',
    round_of_16: 'Round of 16',
    quarter_final: 'Quarter Finals',
    qualifier_1: 'Qualifier 1',
    eliminator: 'Eliminator',
    qualifier_2: 'Qualifier 2',
    semi_final: 'Semi Finals',
    third_place: '3rd Place',
    final: 'Final',
  };

  // Playoffs read Q1 / Eliminator → Q2 → Final; lower cups' matches carry the cup's name
  const roundOrder = ['round_of_32', 'round_of_16', 'quarter_final', 'qualifier_1', 'eliminator', 'qualifier_2',
    'semi_final', 'final', 'third_place'];

  // Filter to only rounds that have matches
  const activeRounds = roundOrder.filter(round => bracket[round] && bracket[round].length > 0);

  return (
    <div className="space-y-6" data-testid="knockout-bracket">
      {/* Horizontal Bracket View */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-6 min-w-max">
          {activeRounds.filter(r => r !== 'third_place').map((round, roundIdx) => (
            <div key={round} className="flex flex-col items-center">
              {/* Round Label */}
              <div className="mb-4 px-4 py-2 bg-[#2B3A67] text-white text-sm font-bold rounded-lg">
                {roundLabels[round]}
              </div>

              {/* Matches */}
              <div 
                className="flex flex-col gap-4 justify-around"
                style={{ 
                  minHeight: round === 'final' ? 'auto' : `${bracket[round].length * 120}px`,
                }}
              >
                {bracket[round].map((match, matchIdx) => (
                  <BracketMatch 
                    key={match.id} 
                    match={match} 
                    isFinal={round === 'final' && !match.group_name}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Third Place Match */}
      {bracket.third_place && bracket.third_place.length > 0 && (
        <div className="border-t border-gray-200 pt-6">
          <div className="flex justify-center">
            <div className="text-center">
              <div className="mb-4 px-4 py-2 bg-amber-600 text-white text-sm font-bold rounded-lg inline-block">
                {roundLabels.third_place}
              </div>
              <div>
                {bracket.third_place.map(match => (
                  <BracketMatch key={match.id} match={match} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-xs text-gray-500 border-t border-gray-100 pt-4">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-green-100 border-2 border-green-500 rounded"></span>
          Winner
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-gray-100 border border-gray-300 rounded"></span>
          Scheduled
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 bg-amber-100 border border-amber-400 rounded"></span>
          TBD
        </span>
      </div>
    </div>
  );
}

function BracketMatch({ match, isFinal = false }) {
  const isCompleted = match.match_status === 'completed';
  
  return (
    <div 
      className={`w-56 bg-white border-2 rounded-xl overflow-hidden shadow-sm ${
        isFinal ? 'border-amber-400' : 'border-gray-200'
      }`}
      data-testid={`bracket-match-${match.bracket_position}`}
    >
      {match.group_name && (
        <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide bg-[#2B3A67]/5 text-[#2B3A67]">
          {match.group_name}
        </div>
      )}
      {/* Team A */}
      <div 
        className={`px-3 py-2 flex items-center justify-between ${
          match.winner_id === match.team_a?.id ? 'bg-green-50 border-l-4 border-green-500' : ''
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {match.team_a?.logo ? (
            <img src={match.team_a.logo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ph-bold ph-user text-gray-400 text-xs"></i>
            </div>
          )}
          <span className={`text-sm truncate ${
            match.team_a?.name !== 'TBD' ? 'font-medium' : 'text-gray-400'
          }`}>
            {match.team_a?.name || 'TBD'}
          </span>
        </div>
        {isCompleted && match.team_a?.score !== null && (
          <span className={`text-sm font-bold ml-2 ${
            match.winner_id === match.team_a?.id ? 'text-green-600' : 'text-gray-500'
          }`}>
            {match.team_a.score}
          </span>
        )}
        {match.winner_id === match.team_a?.id && (
          <i className="ph-fill ph-trophy text-amber-500 text-sm ml-1"></i>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100"></div>

      {/* Team B */}
      <div 
        className={`px-3 py-2 flex items-center justify-between ${
          match.winner_id === match.team_b?.id ? 'bg-green-50 border-l-4 border-green-500' : ''
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {match.team_b?.logo ? (
            <img src={match.team_b.logo} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ph-bold ph-user text-gray-400 text-xs"></i>
            </div>
          )}
          <span className={`text-sm truncate ${
            match.team_b?.name !== 'TBD' ? 'font-medium' : 'text-gray-400'
          }`}>
            {match.team_b?.name || 'TBD'}
          </span>
        </div>
        {isCompleted && match.team_b?.score !== null && (
          <span className={`text-sm font-bold ml-2 ${
            match.winner_id === match.team_b?.id ? 'text-green-600' : 'text-gray-500'
          }`}>
            {match.team_b.score}
          </span>
        )}
        {match.winner_id === match.team_b?.id && (
          <i className="ph-fill ph-trophy text-amber-500 text-sm ml-1"></i>
        )}
      </div>

      {/* Match Info */}
      {match.scheduled_at && (
        <div className="px-3 py-1.5 bg-gray-50 text-[10px] text-gray-500 text-center border-t border-gray-100">
          {new Date(match.scheduled_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}
    </div>
  );
}
